import moment from 'moment';

import type { ScoreModuleStatus, ScoreSeriesEntry } from './TreamsScore.types';
import { toDisplayScore } from './scoreDisplay';
import { SCORE_INTERVAL } from './scoreEndDates';
import { adjustScoresForModuleStatus, aggregateRowScores } from './scoreTotal';

export { buildSeriesEndDates, clampScoreEndDate } from './scoreEndDates';

export type ScoreSeriesPoint = {
	date: Date;
	label: string;
	value: number;
};

type Interval = keyof typeof SCORE_INTERVAL;

// Aggregated card series: each point is the module-adjusted mean over the
// selected rows, matching exactly how the headline number is built.
export const toScoreSeriesPoints = (
	entries: ScoreSeriesEntry[],
	selectedRowIDs: string[],
	moduleStatus: ScoreModuleStatus,
	interval: Interval,
): ScoreSeriesPoint[] => {
	const selected = new Set(selectedRowIDs);
	const { format } = SCORE_INTERVAL[interval];

	return entries.flatMap((entry) => {
		const rows = entry.rows.filter((row) => selected.has(row.id));
		if (rows.length === 0) return [];

		const adjusted = adjustScoresForModuleStatus(aggregateRowScores(rows), moduleStatus);

		return [
			{
				date: entry.endDate,
				label: moment(entry.endDate).format(format),
				value: toDisplayScore(adjusted.total.score),
			},
		];
	});
};

// Per-row trend values for the comparison-table sparklines, chronological and
// module-adjusted so each row's last point matches its score column.
export const toRowTrends = (
	entries: ScoreSeriesEntry[],
	moduleStatus: ScoreModuleStatus,
): Map<string, number[]> => {
	const trends = new Map<string, number[]>();

	for (const entry of entries) {
		for (const row of entry.rows) {
			const adjusted = adjustScoresForModuleStatus(row.scores, moduleStatus);
			const values = trends.get(row.id) ?? [];

			values.push(toDisplayScore(adjusted.total.score));
			trends.set(row.id, values);
		}
	}

	return trends;
};
